'use client';

import {
  useState,
} from 'react';

import { camelizeKeys } from 'humps';

import {
  WagContentApiEndpoint,
  WAG_CONTENT_API_URL,
  WAG_WALKING_ASSETS_BASE_URL,
} from '../constants';
import { tryCatch } from '../utils/tryCatch';
import { httpGet, HttpMethod } from '../wag-react/common/api';

type Options = {
  type: string;
  fileName: string;
  destination: string;
  file: File | Blob;
};

type S3SignatureResult = {
  filePath: string;
  data: {
    url: string;
    fields: Record<
      | 'policy'
      | 'key'
      | 'acl'
      | 'xAmzDate'
      | 'xAmzAlgorithm'
      | 'xAmzSignature'
      | 'contentType'
      | 'xAmzCredential',
      string
    >;
  };
};
type S3UploadOptions = Options & S3SignatureResult;

const getSignature = async ({
  destination,
  fileName,
  type,
}: Options): Promise<S3SignatureResult> => {
  const response = await httpGet<S3SignatureResult>(WagContentApiEndpoint.GetAWSSIgnature, {
    baseURL: WAG_CONTENT_API_URL,
    params: {
      type,
      name: fileName,
      dest: destination,
    },
    headers: {
      'Content-Type': 'application/json',
    },
    responseType: 'json',
  });
  const signature = camelizeKeys(response.data) as S3SignatureResult;
  return signature;
};

const upload = ({
  data: {
    url,
    fields: {
      key,
      acl,
      contentType,
      policy,
      xAmzSignature,
      xAmzCredential,
      xAmzDate,
      xAmzAlgorithm,
    },
  },
  file,
}: S3UploadOptions) => new Promise<void>((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  const formData = new FormData();

  formData.append('key', key);
  formData.append('acl', acl);
  formData.append('Content-Type', contentType);
  formData.append('policy', policy);
  formData.append('X-Amz-Signature', xAmzSignature);
  formData.append('X-Amz-Credential', xAmzCredential);
  formData.append('X-Amz-Date', xAmzDate);
  formData.append('X-Amz-Algorithm', xAmzAlgorithm);
  formData.append('file', file);

  xhr.onerror = () => reject(new Error('An error occurred while uploading the file'));

  xhr.onreadystatechange = () => {
    if (xhr.readyState !== XMLHttpRequest.DONE) {
      return;
    }
    // Upload is done; No need to return anything
    resolve();
  };

  xhr.open(HttpMethod.Post, url, true);
  xhr.send(formData);
});

export const s3Upload = async (options: Options) => {
  const [data] = await tryCatch(async () => {
    const signature = await getSignature(options);
    await upload({
      ...options,
      ...signature,
    });
    const url = [
      WAG_WALKING_ASSETS_BASE_URL,
      '/',
      signature.data.fields.key,
    ].join('');

    return url;
  });
  return data;
};

export const useS3Upload = () => {
  const [loading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState<string>(null);

  const execute = async (options: Options) => {
    setIsLoading(true);
    try {
      const signature = await getSignature(options);
      await upload({
        ...options,
        ...signature,
      });

      const url = [
        WAG_WALKING_ASSETS_BASE_URL,
        '/',
        signature.data.fields.key,
      ].join('');

      setData(url);
    } catch (_error) {
      setError(_error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    loading,
    error,
    execute,
    data,
    isSuccess: !!data,
  };
};
